import { Component, OnDestroy, OnInit } from '@angular/core';
import { SciChartSurface } from 'scichart/Charting/Visuals/SciChartSurface';
import { NumericAxis } from 'scichart/Charting/Visuals/Axis/NumericAxis';
import { XyDataSeries } from 'scichart/Charting/Model/XyDataSeries';
import { FastLineRenderableSeries } from 'scichart/Charting/Visuals/RenderableSeries/FastLineRenderableSeries';
import { TemperatureDataRow } from '../models/temperature-data-row';
import { ProjectService } from '../services/project.service';
import { ChartCaptureService } from '../services/chart-capture.service';
import { ENumericFormat } from 'scichart/types/NumericFormat';
import { EAutoRange } from 'scichart/types/AutoRange';
import { EAxisAlignment } from 'scichart/types/AxisAlignment';
import { NumberRange } from 'scichart/Core/NumberRange';
import { CursorModifier } from 'scichart/Charting/ChartModifiers/CursorModifier';
import { ZoneDataRow } from '../models/zone-data-row';
import {
  BoxAnnotation,
  ELabelPlacement,
  HeatmapColorMap,
  RolloverModifier,
  RubberBandXyZoomModifier,
  TSciChart,
  TextAnnotation,
  UniformHeatmapDataSeries,
  UniformHeatmapRenderableSeries,
  VerticalLineAnnotation,
  ZoomPanModifier,
  zeroArray2D,
} from 'scichart';
import { Subscription, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ProjectTemperatureData } from '../models/project-temperature-data';
import { ProjectZoneData } from '../models/project-zone-data';
import { MarkerInfo } from '../models/marker-info';

@Component({
  selector: 'app-temperature',
  templateUrl: './temperature.component.html',
  styleUrls: ['./temperature.component.scss'],
})
export class TemperatureComponent implements OnInit, OnDestroy {
  errorMessage = '';
  tempDataLoaded: boolean = false;
  zoneDataLoaded: boolean = false;

  // Temp Chart
  temperatureDataRows: TemperatureDataRow[] = [];
  xyDataSeries: XyDataSeries = {} as XyDataSeries;
  tick: number = 0;
  tempData: ProjectTemperatureData = {} as ProjectTemperatureData;
  tempAboveXPoint: number = 70;
  sciChartSurface?: SciChartSurface; // Store reference to chart surface
  isInitialLoad: boolean = true; // Track if this is the first load

  rolloverModifier?: RolloverModifier;
  zoomPanModifier?: ZoomPanModifier;
  rubberBandZoomModifier?: RubberBandXyZoomModifier;

  // Zone Chart
  zoneDataRows: ZoneDataRow[] = [];
  heatmapDataSeries1: UniformHeatmapDataSeries = {} as UniformHeatmapDataSeries;
  heatmapDataSeries2: UniformHeatmapDataSeries = {} as UniformHeatmapDataSeries;
  zoneData: ProjectZoneData = {} as ProjectZoneData;
  zoneSciChartSurface?: SciChartSurface; // Store reference to zone chart surface

  heatMaps: number[][][] = [];
  isRunning: boolean = false;

  rangeSliderLeftVal: number = 0;
  rangeSliderRightVal: number = 100;
  showRangeSlider: boolean = false;

  // Chart Controls Events
  public onZoomOut?: () => void;
  public onAddMarker?: () => void;
  public onRangeSliderChange?: (left: number, right: number) => void;

  v_annotations: VerticalLineAnnotation[] = [];

  boxAnnotation: BoxAnnotation = {} as BoxAnnotation;

  selectedProjectSubscription: Subscription = {} as Subscription;
  tempDataSubscription: Subscription = {} as Subscription;
  tempRTDataSubscription: Subscription = {} as Subscription;
  tickSubscription: Subscription = {} as Subscription;
  vert_annot_positionsSubscription: Subscription = {} as Subscription;

  constructor(public projectService: ProjectService, private chartCapture: ChartCaptureService) {}

  ngOnInit() {
    //console.log('Temp OnInit called');
    this.initSciChart();
    this.setupProjectSubscription();
    this.createTempGraph();
    //this.createZoneGraphs();
  }

  ngOnDestroy(): void {
    // Dispose of the chart surfaces to free up resources
    if (this.zoneSciChartSurface) {
      this.chartCapture.unregister('zones', this.zoneSciChartSurface);
      this.zoneSciChartSurface.delete();
    }
    if (this.sciChartSurface) {
      this.sciChartSurface.delete();
    }

    this.selectedProjectSubscription.unsubscribe();
    this.tempDataSubscription.unsubscribe();
    this.tempRTDataSubscription.unsubscribe();
    //this.tickSubscription.unsubscribe();
    this.vert_annot_positionsSubscription.unsubscribe();
  }

  // Setup subscription to recreate chart when project changes
  setupProjectSubscription() {
    this.selectedProjectSubscription =
      this.projectService.selectedProject.subscribe(
        (projectName) => {
          if (projectName && projectName.length > 0) {
            console.log('Temp > Project changed to:', projectName);
            console.log('Temp > Current unit setting:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');
            
            // Skip recreation on initial load (graph is already being created in ngOnInit)
            if (this.isInitialLoad) {
              this.isInitialLoad = false;
              console.log('Temp > Skipping recreation on initial load');
              return;
            }
            
            // Recreate the graph with the new project's unit settings
            this.recreateTempGraph();
          }
        },
        (error) => (this.errorMessage = error)
      );
  }

  // Recreate the temperature graph (called when project changes)
  async recreateTempGraph() {
    console.log('Temp > Recreating graph with units:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');

    // Dispose of the existing charts if they exist
    if (this.zoneSciChartSurface) {
      this.zoneSciChartSurface.delete();
      this.zoneSciChartSurface = undefined;
    }
    if (this.sciChartSurface) {
      console.log('Temp > Disposing existing chart');
      this.sciChartSurface.delete();
      this.sciChartSurface = undefined;
    }

    // Clear data
    this.heatMaps = [];
    this.heatmapDataSeries1 = {} as UniformHeatmapDataSeries;
    this.heatmapDataSeries2 = {} as UniformHeatmapDataSeries;

    // Recreate the chart
    await this.createTempGraph();
  }

  async initSciChart() {
    SciChartSurface.useWasmFromCDN();
    SciChartSurface.setRuntimeLicenseKey(environment.sciChartKey);
  }

  // Temperatere Graph ===========================================================================

  async createTempGraph() {
    console.log('Temp > Creating graph with units:', this.projectService.isMetricUnit ? 'Metric (m, °C)' : 'Imperial (ft, °F)');
    
    // Create the SciChartSurface in the div 'scichart-root'
    const { sciChartSurface, wasmContext } = await SciChartSurface.create(
      'temp-graph'
    );

    // Store reference to the chart surface
    this.sciChartSurface = sciChartSurface;

    //sciChartSurface.load

    // Create an X,Y Axis and add to the chart
    var xAxis_Temp = new NumericAxis(wasmContext, {
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'm' : 'ft',
    });

    // TODO: Set in VericureBlazor Code but not working
    //xAxis.labelProvider.formatLabel = this.formatxAxisTemperatureGraph;

    var yAxis_Temp = new NumericAxis(wasmContext, {
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit
        ? String.fromCharCode(176).concat('C')
        : String.fromCharCode(176).concat('F'),
    });
    yAxis_Temp.axisAlignment = EAxisAlignment.Left;
    yAxis_Temp.growBy = new NumberRange(0.2, 0.2);

    sciChartSurface.xAxes.add(xAxis_Temp);
    sciChartSurface.yAxes.add(yAxis_Temp);

    // Add Chart Controls Modifiers
    // Create new modifier instances to avoid "already attached" errors
    this.rolloverModifier = new RolloverModifier();
    this.rolloverModifier.rolloverLineStroke = '#41B972';
    this.rolloverModifier.rolloverLineStrokeThickness = 2;
    this.rolloverModifier.showRolloverLine = true;
    sciChartSurface.chartModifiers.add(this.rolloverModifier);

    this.rubberBandZoomModifier = new RubberBandXyZoomModifier();
    this.rubberBandZoomModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.rubberBandZoomModifier);

    this.zoomPanModifier = new ZoomPanModifier();
    this.zoomPanModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.zoomPanModifier);

    // Create a line series with some data
    this.xyDataSeries = new XyDataSeries(wasmContext);

    const lineSeries = new FastLineRenderableSeries(wasmContext, {
      stroke: '#ffffff',
      strokeThickness: 2,
      dataSeries: this.xyDataSeries,
    });
    lineSeries.rolloverModifierProps.tooltipColor = '#F3FEF8';
    lineSeries.rolloverModifierProps.tooltipTextColor = '#3B3D3C';
    sciChartSurface.renderableSeries.add(lineSeries);

    this.boxAnnotation = new BoxAnnotation({
      fill: '#279B2755',
      stroke: '#279B27',
      strokeThickness: 1,
      x1: this.tempData.CRB,
      x2: this.tempData.CRE,
      y1: 10,
      y2: 20,
    });
    sciChartSurface.annotations.add(this.boxAnnotation);

    // Subscribe to temperature data updates
    // Note: Don't subscribe to selectedProject here as it's handled in setupProjectSubscription()
    // Unsubscribe from previous subscription if it exists to prevent memory leaks
    if (this.tempDataSubscription && typeof this.tempDataSubscription.unsubscribe === 'function') {
      this.tempDataSubscription.unsubscribe();
    }
    this.tempDataSubscription = this.projectService.temperatureData.subscribe(
      (data) => {
        if (data == null) {
          console.log('Error in Temp Comp: Temp data is null.');
          return;
        } else if (data.TRC == null) {
          console.log('Error in Temp Comp: Temp data > TRC is null.');
          return;
        }

        this.tempData = data;
        this.temperatureDataRows = data.TRC;
        this.tempAboveXPoint = data.CT;

        console.log('LSS:  ' + this.tempData.LSS);
        console.log('LES:  ' + this.tempData.LES);
        console.log('CRB:  ' + this.tempData.CRB);
        console.log('CRE:  ' + this.tempData.CRE);

        this.updateTempChartForTick('0', 'temperatureData.subscribe');

        this.projectService.loadMarkers(data.MAR);
        this.updateBoxArea(this.boxAnnotation, this.tempAboveXPoint);
        sciChartSurface.zoomExtents();

        this.adjustYAxisVisibleRange(yAxis_Temp);
      },
      (error) => (this.errorMessage = error)
    );

    // Unsubscribe from previous subscription if it exists to prevent memory leaks
    if (this.tempRTDataSubscription && typeof this.tempRTDataSubscription.unsubscribe === 'function') {
      this.tempRTDataSubscription.unsubscribe();
    }
    this.tempRTDataSubscription =
      this.projectService.temperatureRTData.subscribe(
        (data) => {
          console.log('temp comp > temperatureRTData.subscribe');
          if (data == null) {
            console.log('Error in Temp Comp: Temp RT data is null.');
            return;
          } else if (data.TRC == null) {
            console.log('Error in Temp Comp: Temp RT data > TRC is null.');
            return;
          }

          if (data.TRC.length > 0) {
            this.temperatureDataRows.push(data.TRC[0]);

            console.log(
              'temp comp > pushed next TimeStamp Data. Count: ' +
                this.temperatureDataRows.length
            );

            var lastPos = this.temperatureDataRows.length - 1;
            this.updateTempChartForTick(
              lastPos.toString(),
              'temperatureData.subscribe'
            );

            this.updateBoxArea(this.boxAnnotation, this.tempAboveXPoint);

            sciChartSurface.zoomExtents();
            this.adjustYAxisVisibleRange(yAxis_Temp);
          }
        },
        (error) => (this.errorMessage = error)
      );

    // this.tickSubscription = this.projectService.tickPosition.subscribe(
    //   (data) => {
    //     this.updateTempChartForTick(data.toString(), 'tickPosition.subscribe');
    //     sciChartSurface.zoomExtents();

    //     this.adjustYAxisVisibleRange(yAxis_Temp);
    //   },
    //   (error) => (this.errorMessage = error)
    // );

    // Listen to Control Events:
    this.onZoomOut = () => {
      sciChartSurface.zoomExtents();
      if (this.showRangeSlider) {
        this.toggleRangeSlider();
      }

      this.adjustYAxisVisibleRange(yAxis_Temp);
    };

    // Unsubscribe from previous subscription if it exists to prevent memory leaks
    if (this.vert_annot_positionsSubscription && typeof this.vert_annot_positionsSubscription.unsubscribe === 'function') {
      this.vert_annot_positionsSubscription.unsubscribe();
    }
    this.vert_annot_positionsSubscription =
      this.projectService.vert_annot_positions.subscribe(
        (data) => {
          console.log('TemperatureComponent > Markers subscription received:', data);
          console.log('TemperatureComponent > Number of markers:', data?.length);
          if (data && data.length > 0) {
            this.updateVerticalAnnotations(sciChartSurface, data);
          } else {
            console.log('TemperatureComponent > No markers to display or data is empty');
          }
        },
        (error) => (this.errorMessage = error)
      );
    
    // Check if markers already exist (in case they were loaded before subscription)
    const existingMarkers = this.projectService.getVerticalAnnotationPositions();
    if (existingMarkers && existingMarkers.length > 0) {
      console.log('TemperatureComponent > Found existing markers on initialization:', existingMarkers);
      this.updateVerticalAnnotations(sciChartSurface, existingMarkers);
    }

    this.onAddMarker = () => {
      this.projectService.addMarker();
    };

    this.createZoneGraphs(xAxis_Temp);
  }

  formatxAxisTemperatureGraph(dataValue: number) {
    //adjust the value that user sees on the axis from 0 to end of range instead of from startRange to endRange.
    return (dataValue - this.tempData.CRB).toFixed(2) +
      this.projectService.isMetricUnit
      ? 'm'
      : 'ft';
  }

  updateTempChartForTick(val: string, source: string) {
    const sliderVal: number = +val;
    this.tick = sliderVal;

    // Update Temp Graph
    if (this.tempDataLoaded) {
      this.xyDataSeries.clear();
    }

    if (this.temperatureDataRows.length == 0) {
      this.tempDataLoaded = false;
      return;
    } else if (this.temperatureDataRows[0].TR == null) {
      this.tempDataLoaded = false;
      return;
    } else if (this.temperatureDataRows[0].TR.length == 0) {
      this.tempDataLoaded = false;
      return;
    } else {
      this.tempDataLoaded = true;
    }

    const startDataPoint = this.tempData.LSS;
    const endDataPoint = this.tempData.LES;

    const xFactor =
      (this.tempData.CRE - this.tempData.CRB) / (endDataPoint - startDataPoint);

    var xValues: number[] = new Array(endDataPoint - startDataPoint);
    var yValues: number[] = new Array(endDataPoint - startDataPoint);

    for (let j = startDataPoint; j < endDataPoint; j++) {
      xValues[j - startDataPoint] = (j - startDataPoint) * xFactor;
      yValues[j - startDataPoint] = this.temperatureDataRows[sliderVal].TR[j];
    }

    this.xyDataSeries.appendRange(xValues, yValues);
  }

  // Zone Graph ===========================================================================

  async createZoneGraphs(tempXAsix: NumericAxis) {
    // Create the SciChartSurface
    const { sciChartSurface: sciChartSurface1, wasmContext: wasmContext1 } =
      await SciChartSurface.create('zone-graph-1');
    const { sciChartSurface: sciChartSurface2, wasmContext: wasmContext2 } =
      await SciChartSurface.create('zone-graph-2');

    // Store reference and register for PDF export capture
    this.zoneSciChartSurface = sciChartSurface1;
    this.chartCapture.register('zones', sciChartSurface1, 'zone-graph-1');

    // Create an X,Y Axis and add to the chart
    const xAxis = new NumericAxis(wasmContext1, {
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'm' : 'ft',
      axisAlignment: EAxisAlignment.Top,
    });

    const yAxis = new NumericAxis(wasmContext1, {
      autoRange: EAutoRange.Always,
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'C' : 'F',
      drawLabels: false,
      axisAlignment: EAxisAlignment.Left,
    });

    sciChartSurface1.xAxes.add(xAxis);
    sciChartSurface1.yAxes.add(yAxis);

    // Create an X,Y Axis and add to the chart
    const xAxis2 = new NumericAxis(wasmContext1, {
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'm' : 'ft',
      axisAlignment: EAxisAlignment.Top,
      isVisible: false,
    });

    const yAxis2 = new NumericAxis(wasmContext1, {
      autoRange: EAutoRange.Always,
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'C' : 'F',
      drawLabels: false,
      axisAlignment: EAxisAlignment.Left,
      isVisible: false,
    });

    sciChartSurface2.xAxes.add(xAxis2);
    sciChartSurface2.yAxes.add(yAxis2);

    // Api subscription
    this.projectService.zoneData.subscribe(
      (data) => {
        if (data == null) {
          console.log('Error in Temp Comp: Zone data is null.');
          return;
        } else if (data.ZRC == null) {
          console.log('Error in Temp Comp: Zone data > ZRC is null.');
          return;
        }

        this.zoneData = data;
        this.zoneDataRows = data.ZRC;

        const startDataPoint = this.tempData.LSS;
        const endDataPoint = this.tempData.LES;

        const xFactor =
          (this.tempData.CRE - this.tempData.CRB) /
          (endDataPoint - startDataPoint);

        this.heatMaps = [];

        for (let i = 0; i < this.zoneDataRows.length; i++) {
          const heatMapData = zeroArray2D([1, endDataPoint - startDataPoint]);
          for (let j = startDataPoint; j < endDataPoint; j++) {
            heatMapData[0][j - startDataPoint] = this.zoneDataRows[i].ZR[j];
          }

          this.heatMaps[i] = heatMapData;
        }

        this.heatmapDataSeries1 = new UniformHeatmapDataSeries(wasmContext1, {
          xStart: 0,
          xStep: xFactor,
          yStart: 100,
          yStep: 1,
          zValues: this.heatMaps[0],
        });

        this.heatmapDataSeries2 = new UniformHeatmapDataSeries(wasmContext2, {
          xStart: 0,
          xStep: xFactor,
          yStart: 100,
          yStep: 1,
          zValues: this.heatMaps[0],
        });

        this.setupZoneDataSeries(
          sciChartSurface1,
          wasmContext1,
          this.heatmapDataSeries2
        );
        this.setupZoneDataSeries(
          sciChartSurface2,
          wasmContext2,
          this.heatmapDataSeries1
        );
      },
      (error) => (this.errorMessage = error)
    );

    // Zone RT Data Subscription
    this.projectService.zoneRTData.subscribe(
      (data) => {
        if (data == null) {
          console.log('Error in Temp Comp: Zone data is null.');
          return;
        } else if (data.ZRC == null) {
          console.log('Error in Temp Comp: Zone data > ZRC is null.');
          return;
        }

        if (data.ZRC.length > 0) {
          this.zoneDataRows.push(data.ZRC[0]);
          console.log(
            'temp comp > pushed next Zone Data. Count: ' +
              this.zoneDataRows.length
          );
        } else {
          return;
        }

        const startDataPoint = this.tempData.LSS;
        const endDataPoint = this.tempData.LES;

        const xFactor =
          (this.tempData.CRE - this.tempData.CRB) /
          (endDataPoint - startDataPoint);

        this.heatMaps = [];

        for (let i = 0; i < this.zoneDataRows.length; i++) {
          const heatMapData = zeroArray2D([1, endDataPoint - startDataPoint]);
          for (let j = startDataPoint; j < endDataPoint; j++) {
            heatMapData[0][j - startDataPoint] = this.zoneDataRows[i].ZR[j];
          }

          this.heatMaps[i] = heatMapData;
        }

        this.heatmapDataSeries1 = new UniformHeatmapDataSeries(wasmContext1, {
          xStart: 0,
          xStep: xFactor,
          yStart: 100,
          yStep: 1,
          zValues: this.heatMaps[0],
        });

        this.heatmapDataSeries2 = new UniformHeatmapDataSeries(wasmContext2, {
          xStart: 0,
          xStep: xFactor,
          yStart: 100,
          yStep: 1,
          zValues: this.heatMaps[0],
        });

        this.setupZoneDataSeries(
          sciChartSurface1,
          wasmContext1,
          this.heatmapDataSeries2
        );
        this.setupZoneDataSeries(
          sciChartSurface2,
          wasmContext2,
          this.heatmapDataSeries1
        );
      },
      (error) => (this.errorMessage = error)
    );

    this.projectService.tickPosition.subscribe(
      (data) => {
        this.updateZoneChartForTick(data.toString());
      },
      (error) => (this.errorMessage = error)
    );

    this.onRangeSliderChange = (left, right) => {
      //console.log(left + '-' + right);
      const linerLength = this.tempData.CRE - this.tempData.CRB;

      const visibleLeft = Math.round((left / 100) * linerLength);
      const visibleRight = Math.round((right / 100) * linerLength);

      tempXAsix.visibleRange = new NumberRange(visibleLeft, visibleRight);
      xAxis.visibleRange = new NumberRange(visibleLeft, visibleRight);
      xAxis2.visibleRange = new NumberRange(visibleLeft, visibleRight);
    };
  }

  setupZoneDataSeries(
    sciChartSurface: SciChartSurface,
    wasmContext: TSciChart,
    heatMapDataSeries: UniformHeatmapDataSeries
  ) {
    const heatmapSeries = new UniformHeatmapRenderableSeries(wasmContext, {
      dataSeries: heatMapDataSeries,
      colorMap: new HeatmapColorMap({
        minimum: 0, // min value in the zValues (data) to map to offset 0 in the colormap. see GraphViewModel.cs ZoneGradient is define there and is hard coded.
        maximum: 4, // max value in the zValues (data) to map to offset 1 in the colormap
        gradientStops: [
          { offset: 0, color: '#0000FF' }, // Blue
          { offset: 0.2, color: '#0000FF' },
          { offset: 0.2, color: '#FFA500' }, // Orange
          { offset: 0.4, color: '#FFA500' },
          { offset: 0.4, color: '#32CD32' }, // Green
          { offset: 0.8, color: '#32CD32' },
          { offset: 0.8, color: '#FF0000' }, // Red
          { offset: 1.0, color: '#FF0000' },
        ],
      }),
    });

    sciChartSurface.renderableSeries.add(heatmapSeries);
  }

  updateZoneChartForTick(val: string) {
    const sliderVal: number = +val;
    this.tick = sliderVal;

    // Clear Graph
    if (this.zoneDataLoaded) {
      this.heatmapDataSeries1.clear();
      this.heatmapDataSeries2.clear();
    }

    if (this.heatMaps.length == 0) {
      this.zoneDataLoaded = false;
      return;
    } else {
      this.tempDataLoaded = true;
    }

    // Update Heatmap Graph
    this.heatmapDataSeries1.setZValues(this.heatMaps[sliderVal]);
    this.heatmapDataSeries2.setZValues(this.heatMaps[sliderVal]);
  }

  setRollOver(val: boolean) {
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = val;
  }
  setPan(val: boolean) {
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = val;
  }
  setZoom(val: boolean) {
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = val;
  }

  zoomOut() {
    if (!this.onZoomOut) return;
    this.onZoomOut();
  }

  addMarker() {
    if (!this.onAddMarker) return;
    this.onAddMarker();
  }

  convertRgbStringToColor(rgbString: string): string {
    // Convert "r, g, b" string to hex format for SciChart
    if (!rgbString) {
      return '#7F30F3'; // Default color
    }
    
    // If it's already in hex format, return as is
    if (rgbString.startsWith('#')) {
      return rgbString;
    }
    
    // Parse "r, g, b" string and convert to hex
    const parts = rgbString.split(',').map(s => s.trim());
    if (parts.length !== 3) {
      return '#7F30F3'; // Default color if format is invalid
    }
    
    const r = parseInt(parts[0]);
    const g = parseInt(parts[1]);
    const b = parseInt(parts[2]);
    
    // Convert to hex format
    const toHex = (n: number) => {
      const hex = n.toString(16).padStart(2, '0');
      return hex;
    };
    
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  updateVerticalAnnotations(sciChart: SciChartSurface, markers: MarkerInfo[]) {
    console.log('=== updateVerticalAnnotations START ===');
    console.log('updateVerticalAnnotations called with markers:', markers);
    console.log('Number of markers:', markers?.length);
    console.log('SciChart surface:', sciChart);
    console.log('Current annotations count before clear:', sciChart.annotations.size());
    
    // Clear all annotations except the boxAnnotation
    const annotationsToRemove: any[] = [];
    sciChart.annotations.asArray().forEach(ann => {
      if (ann !== this.boxAnnotation) {
        annotationsToRemove.push(ann);
      }
    });
    annotationsToRemove.forEach(ann => sciChart.annotations.remove(ann));
    console.log('Marker annotations cleared, keeping box annotation');

    if (!markers || markers.length === 0) {
      console.log('No markers to add - exiting');
      console.log('=== updateVerticalAnnotations END ===');
      return;
    }

    markers.forEach((mar, index) => {
      console.log(`Processing marker ${index}:`, mar);
      
      // Convert RGB string "r, g, b" to format SciChart understands
      const colorString = this.convertRgbStringToColor(mar.C);
      console.log(`  Position: ${mar.P}`);
      console.log(`  Color: "${mar.C}" -> converted to "${colorString}"`);
      
      const annt = new VerticalLineAnnotation({
        labelPlacement: ELabelPlacement.Axis,
        showLabel: true,
        stroke: colorString,
        strokeThickness: 3,
        x1: mar.P,
        axisLabelFill: colorString,
        axisFontSize: 14,
        isEditable: true,
        isSelected: false,
        onDragStarted: () => {
          // Increase thickness during drag for better visibility
          annt.strokeThickness = 6;
        },
        onDragEnded: () => {
          // Update the position in the internal array
          mar.P = +annt.x1.toFixed(2);
          // Persist to API
          this.projectService.updateVerticalAnnotationPosition(
            index,
            +annt.x1.toFixed(2)
          );
          // Reset to normal thickness after drag
          annt.strokeThickness = 3;
        },
      });

      console.log(`  Adding annotation to chart for marker ${index}`);
      sciChart.annotations.add(annt);
      console.log(`  Annotation added successfully. Total annotations: ${sciChart.annotations.size()}`);
    });
    
    console.log('Final annotations count:', sciChart.annotations.size());
    console.log('=== updateVerticalAnnotations END ===');
  }

  updateBoxArea(annot: BoxAnnotation, y1Val: number) {
    annot.x1 = 0;
    annot.x2 = this.tempData.CRE;
    annot.y1 = y1Val;
    annot.y2 = y1Val + 150;
  }

  toggleRangeSlider() {
    this.showRangeSlider = !this.showRangeSlider;

    if (!this.onRangeSliderChange) return;
    this.onRangeSliderChange(0, 100);
  }

  updateChartVisibleRange(leftVal: string, rightVal: string) {
    if (!this.onRangeSliderChange) return;

    if (+leftVal <= +rightVal) {
      this.rangeSliderLeftVal = +leftVal;
      this.rangeSliderRightVal = +rightVal;
    } else {
      this.rangeSliderLeftVal = +rightVal;
      this.rangeSliderRightVal = +leftVal;
    }

    this.onRangeSliderChange(this.rangeSliderLeftVal, this.rangeSliderRightVal);
  }

  toggleHelpOverlay() {
    this.projectService.isHelpOverlayVisible =
      !this.projectService.isHelpOverlayVisible;
  }

  adjustYAxisVisibleRange(yAxis_Temp: NumericAxis) {
    var visibleMin = 0;
    if (yAxis_Temp.visibleRange.min < -10) {
      visibleMin = -10;
    }
    if (yAxis_Temp.visibleRange.max < this.tempAboveXPoint + 10) {
      yAxis_Temp.visibleRange = new NumberRange(
        visibleMin,
        this.tempAboveXPoint + 10
      );
    } else if (yAxis_Temp.visibleRange.min < -10) {
      yAxis_Temp.visibleRange = new NumberRange(
        visibleMin,
        yAxis_Temp.visibleRange.max
      );
    }
  }
}
